import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react';
import Settings from 'settings';
import { Window } from 'controls/react/kendoWrappers';
import { translator } from 'core/localization/localization';
import classnames from 'classnames';
import { Spin } from 'antd';
import { Section } from 'controls/react/layout/section';
import { QualifierWizardStore } from './qualifierWizardStore';
import PageSourceWindow from './pageSourceWindow';

import './configErrorWindow.less';

const i18n = translator({
	'Test': { no: 'Test' },
	'Post validate': { no: 'Post validering', en: 'Post validate' },
	'Click here to view page source': { no: 'Klikk for å se sidekilde' },
});

export interface ConfigErrorWindowProps {
	store: QualifierWizardStore;
	name?: string;
	// not named `position` — that prop name is globally reserved by controls/react/layout/toolbar's ambient
	// JSX.IntrinsicAttributes augmentation (used for Toolbar child placement) and would collide here.
	fallbackPosition?: { top: number; left: number; width: number; height: number };
}

export const ConfigErrorWindow = observer(({ store, name, fallbackPosition }: ConfigErrorWindowProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const [showSourceWindow, setShowSourceWindow] = useState(false);

	const { validation, inProgress } = store;

	const width = store.qualifierWidth || fallbackPosition?.width;
	const height = store.qualifierHeight || fallbackPosition?.height;
	const top = store.qualifierTop ?? fallbackPosition?.top;
	const left = store.qualifierLeft ?? fallbackPosition?.left;

	const windowWidth = 450;
	const windowHeight = 300;

	let windowTop = top + height / 2;
	const contentHeight = window.innerHeight;
	if (windowTop + windowHeight + 32 > contentHeight) {
		windowTop = contentHeight - windowHeight - 60;
	}

	const asideWidth = $('.aside').width();
	const contentWidth = window.innerWidth;
	let windowLeft = left - windowWidth - 10;
	if (windowLeft < asideWidth) {
		if (left + width + windowWidth < contentWidth) {
			windowLeft = left + width + 10;
		} else {
			windowLeft = left + width - windowWidth - 20;
		}
	}

	if (!validation?.show || height === undefined || top === undefined || left === undefined) {
		return null;
	}

	let title: string;
	const qualifierName = (store.qualifierFieldValue('name') as string) || name;

	switch (validation.type) {
		case 'test':
			title = `${i18n('Test')} ${qualifierName}`;
			break;
		case 'postValidate':
			title = `${i18n('Post validate')} ${qualifierName}`;
			break;
	}

	let url = '';
	if (validation.attachment) {
		const { id, name: attachmentName, mimeType } = validation.attachment;
		url = Settings.serverPath + `services/qualifiers/attachments/${id}?mimeType=${mimeType}&name=${attachmentName}`;
	}

	const Content = () => (
		<Section appearance={'none'} height={'fit'}>
			{validation.attachment && (
				<div className={'cw_configuration_attachment_notification'} onClick={() => setShowSourceWindow(true)}>
					{i18n('Click here to view page source')}
				</div>
			)}
			<div className="cw_configuration_error_content" ref={ref}>
				<ul>
					{validation.details?.map((item, index) => (
						<li key={index}>
							{item.message}
							<span
								className={classnames('glyphicons', 'cw_error_status', {
									'ok': item.success,
									'remove-circle': !item.success,
								})}
							/>
						</li>
					))}
				</ul>
			</div>
			{showSourceWindow && <PageSourceWindow url={url} onClose={() => setShowSourceWindow(false)} />}
		</Section>
	);

	return (
		<Window
			className="config-editor-window"
			contentClass="flex"
			title={title}
			modal={false}
			width={windowWidth}
			height={windowHeight}
			top={windowTop}
			left={windowLeft}
			onClose={store.hideValidation}
		>
			{inProgress ? (
				<Spin tip={i18n('Loading...')}>
					<Content />
				</Spin>
			) : (
				<Content />
			)}
		</Window>
	);
});

export default ConfigErrorWindow;
